
import React, { useState, useEffect } from 'react';
import { getHebrewDate, getWeeklyParasha } from '@/utils/pageUtils';
import { TitleConfig } from '@/types/TitleConfig';
import { usePageContext } from '@/hooks/usePageContext';
import { useNavigate } from 'react-router-dom';

interface DisplayHeaderProps {
  titleConfig: TitleConfig;
  isHebrewDefault?: boolean;
  currentUser?: any;
  generateDynamicTitle?: () => React.ReactNode;
  isEditingMode?: boolean;
}

const Clock: React.FC = () => {
  const [time, setTime] = useState<string>('');

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const hours = now.getHours().toString().padStart(2, '0');
      const minutes = now.getMinutes().toString().padStart(2, '0');
      setTime(`${hours}:${minutes}`);
    };

    updateTime();
    const intervalId = setInterval(updateTime, 1000);

    return () => clearInterval(intervalId);
  }, []);

  return (
    <div className="fixed top-2 sm:top-4 right-2 sm:right-4 z-50 bg-white/80 backdrop-blur-sm px-3 sm:px-12 py-1 sm:py-4 rounded-lg shadow-md">
      <div className="text-3xl sm:text-6xl md:text-8xl lg:text-9xl font-bold text-synagogue-blue">{time}</div>
    </div>
  );
};

const DisplayHeader: React.FC<DisplayHeaderProps> = ({ 
  titleConfig, 
  isHebrewDefault = true,
  currentUser,
  generateDynamicTitle,
  isEditingMode
}) => {
  const navigate = useNavigate();
  const { isHebrew, toggleLanguage } = usePageContext();
  const [hebrewDate, setHebrewDate] = useState<string>('');
  const [parasha, setParasha] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const loadHebrewDate = async () => {
      setIsLoading(true);
      try {
        const date = await getHebrewDate(new Date());
        console.log('Loaded Hebrew date:', date);
        
        if (date && date.trim().length > 0) {
          setHebrewDate(date);
        } else {
          console.warn('Empty Hebrew date received, using fallback');
          setHebrewDate('תאריך עברי לא זמין');
        }
      } catch (error) {
        console.error('Error loading Hebrew date:', error);
        setHebrewDate('תאריך עברי לא זמין');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadHebrewDate();
    
    const intervalId = setInterval(loadHebrewDate, 60 * 60 * 1000);
    
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const loadParasha = async () => {
      try {
        const weeklyParasha = await getWeeklyParasha();
        setParasha(weeklyParasha);
      } catch (error) {
        console.error('Error loading parasha:', error);
        setParasha('');
      }
    };
    
    loadParasha();
  }, []);

  const currentLanguage = isHebrew ? 'he' : 'en';

  const getLogoSize = (size?: string) => {
    switch (size) {
      case 'small': return 'h-10 sm:h-24 md:h-36 lg:h-48';
      case 'large': return 'h-16 sm:h-32 md:h-48 lg:h-64';
      default: return 'h-14 sm:h-28 md:h-40 lg:h-56';
    }
  };

  const getFormattedDate = () => {
    if (isLoading) {
      return 'טוען תאריך...';
    }
    
    return hebrewDate || 'תאריך עברי לא זמין';
  };

  return (
    <div className="flex flex-col items-center justify-center p-2 sm:p-4 relative min-h-[100px] sm:min-h-[160px] md:min-h-[200px] lg:min-h-[240px]">
      {/* Clock */}
      <Clock />
      
      {/* Return to Dashboard Button */}
      <div className="fixed top-2 sm:top-4 left-2 sm:left-4 z-50">
        <button
          onClick={() => navigate('/dashboard')}
          className="bg-synagogue-blue hover:bg-blue-700 text-white font-bold py-1 sm:py-2 px-2 sm:px-4 rounded shadow-md text-sm sm:text-base md:text-lg lg:text-xl"
        >
          {isHebrew ? 'חזרה ללוח הבקרה' : 'Back to Dashboard'}
        </button>
      </div>
      
      {/* Logo */}
      {titleConfig.logo?.enabled && titleConfig.logo?.url && (
        <div className="fixed left-2 sm:left-4 top-4 sm:top-8 z-40">
          <img 
            src={titleConfig.logo.url} 
            alt="Logo" 
            className={`${getLogoSize(titleConfig.logo.size)} object-contain`}
            style={{ 
              maxWidth: titleConfig.logo.maxWidth ? `${titleConfig.logo.maxWidth}px` : '300px'
            }}
          />
        </div>
      )}
      
      {/* Title and Date Container */}
      <div className="flex flex-col items-center justify-center w-full">
        {/* Title */}
        {titleConfig.text && (
          <h1 
            className="font-extrabold text-center bg-white/50 backdrop-blur-sm px-3 sm:px-12 py-1 sm:py-3 rounded-lg shadow-md w-[calc(100%-2rem)] sm:w-auto max-w-[calc(100%-4rem)]"
            style={{ 
              fontFamily: titleConfig.titleFontWeight || 'inherit', 
              color: 'black',
              fontSize: 'clamp(1.25rem, 4vw, 6rem)',
              lineHeight: '1',
              textShadow: '2px 2px 4px rgba(0,0,0,0.15)'
            }}
          >
            {titleConfig.text}
          </h1>
        )}
        
        {/* Date - Always show in Hebrew */}
        {titleConfig.showDate && (
          <h2 
            className="font-extrabold text-center bg-white/40 backdrop-blur-sm px-3 sm:px-8 py-1 sm:py-2 rounded-lg shadow-sm w-[calc(100%-2rem)] sm:w-auto max-w-[calc(100%-4rem)] mt-1 sm:mt-2"
            style={{
              fontFamily: titleConfig.dateTimeFontWeight || 'inherit',
              color: 'black',
              fontSize: 'clamp(1rem, 3vw, 3rem)',
              lineHeight: '1',
              textShadow: '1px 1px 3px rgba(0,0,0,0.1)'
            }}
            dir="rtl"
          >
            {getFormattedDate()}
          </h2>
        )}
      </div>
      
      {/* Language Toggle */}
      {titleConfig.subtitle?.enabled && (
        <button 
          onClick={toggleLanguage} 
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 sm:py-2 px-2 sm:px-4 rounded text-sm sm:text-base md:text-lg lg:text-xl mt-1 sm:mt-2"
        >
          {isHebrew ? 'English' : 'עברית'}
        </button>
      )}
    </div>
  );
};

export default DisplayHeader;
